import type { ReactNode } from "react";
import { Collapse } from "@mui/material";
import { MoreInformationSection } from "../sections/MoreInformationSection";
import type { RunRecordBodyProps } from "../../runRecord/RunRecordBody";
import type { DrawerSectionMap } from "./types";

export interface TerminalDrawerBodyProps {
  sections: DrawerSectionMap;
  errorBlock?: ReactNode | null;
  /**
   * Optional "Ended early — <reason>" banner for an aborted run
   * (rejected / timed-out). Rendered at the TOP (hero slot) so the reason is the
   * first thing the AM sees, not buried in the Result card at the bottom.
   */
  earlyExitBlock?: ReactNode | null;
  /**
   * ``*_warning`` banners from the result body (or the has-warnings fallback).
   * Terminal runs carry them too — a CANCELLED run's teardown still writes a
   * real result (client_lock_warning, lock releases, …), and a failed run's
   * warnings often say what to finish by hand. Rendered right after the hero
   * card so they're seen before Run/Steps. Decision 2026-08-10: warnings must
   * be visible in EVERY terminal state.
   */
  warningsBlock?: ReactNode | null;
  /**
   * Optional extra content rendered immediately after the Error/Cancelled hero
   * card (before Run / Steps / secondary sections). Used by plugin-specific
   * blocks like CarrierHandoff and EndorsementHistory.
   */
  extraTopBlock?: ReactNode | null;
  /**
   * The phase / milestone main stream (running-drawer). A ReactNode, not a
   * snapshot: the bodies stay presentation-only and none of them imports
   * `phaseModel.ts`, so the adapter keeps its single-reader rule.
   *
   * Rendered FIRST when present, and it SUPPRESSES the flat steps chip it
   * replaces.
   */
  phaseStream?: ReactNode | null;
  /**
   * The step view (story 26739) — the "what do I do right now" pane for this
   * moment in the run. A ReactNode, not a model: the bodies stay
   * presentation-only, so the host derives the view from `push.status` on
   * every render and hands the finished node down.
   *
   * When present it takes the hero position (ahead of everything else) and,
   * like `phaseStream`, SUPPRESSES the flat steps chip it replaces.
   * Default `undefined` reproduces today's markup exactly — that is what makes
   * the `emuw-step-views` flag-off path a true rollback rather than a second
   * code path.
   */
  stepView?: ReactNode | null;
  /** The run record shown behind the more-information entry point. The SAME
   *  record the standalone /workflows/runs/<id> page renders. */
  record: RunRecordBodyProps;
  debugActions?: ReactNode | null;
  /** Whether the more-information pane is open. Owned by the top-level
   *  drawer component (survives step/phase updates); the entry-point button
   *  itself lives in the sticky footer, not here. */
  moreInfoOpen?: boolean;
  /** Called when the pane's own back button is clicked. */
  onMoreInfoClose?: () => void;
}

export function TerminalDrawerBody({
  sections,
  errorBlock,
  earlyExitBlock,
  warningsBlock,
  extraTopBlock,
  phaseStream,
  stepView,
  record,
  debugActions,
  moreInfoOpen = false,
  onMoreInfoClose,
}: TerminalDrawerBodyProps) {
  return (
    <>
      {/* The phase / milestone main stream, first and outside every
          collapsible. */}
      {/* The phase stream is the ALWAYS-ON progress display (operator
          directive 2026-09-16): a step view no longer displaces it. Both
          render, stream FIRST, so run progress stays the first thing in the
          body and the step view's reassurance sits under it. */}
      {phaseStream ?? null}
      {stepView ?? null}

      {/* Ended-early banner (rejected / timed-out) — at the VERY TOP so the abort
          reason is the first thing seen, above the Error/Result cards. */}
      {earlyExitBlock ?? null}

      {/* Error or cancelled card — hero */}
      <Collapse in={!!errorBlock} unmountOnExit timeout={400}>
        <div className="mb-1">{errorBlock ?? <span />}</div>
      </Collapse>

      {/* *_warning banners — directly under the hero so a terminal run's
          caveats (attach manually, issue manually, record may be locked) are
          read together with why it ended. */}
      {warningsBlock ?? null}

      {/* AC-7: `extraTopBlock` is a CHILD of the entry point, not a
          primary-body region — passed into extraBlocks below. */}

      {/* The submitted request, in the PRIMARY body — on every
          family, including HITL and terminal (AC-8 / AC-9). */}
      {sections.request ?? null}

      {/* Result renders for EVERY terminal state that provides it — including
          cancelled, superseding AC-12's "cancelled has no Result section"
          (decision 2026-08-10): a cancelled run's teardown writes a real
          result, and hiding it hid the warnings inside it. It renders INSIDE
          the entry point, via extraBlocks. */}
      <MoreInformationSection
        record={record}
        open={moreInfoOpen}
        onClose={() => onMoreInfoClose?.()}
        extraBlocks={
          <>
            {debugActions ?? null}
            {extraTopBlock ?? null}
          </>
        }
      />
    </>
  );
}

export default TerminalDrawerBody;
